import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

export default function HostEvents({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [existing, setExisting] = useState<any | null>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [uid, setUid] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const uid = session?.user?.id || null;
      const email = session?.user?.email || '';
      if (!uid) return;
      setUid(uid);
      const { data: req } = await supabase
        .from('organizer_requests')
        .select('*')
        .eq('user_id', uid)
        .order('created_at', { ascending: false })
        .limit(1);
      const row = req && req.length ? req[0] : null;
      setExisting(row);
      setName(row?.name || email.split('@')[0]);
      setContactEmail(row?.contact_email || email);
      setImageUrl(row?.image_url || '');
      setLogoUrl(row?.logo_url || '');
      setDescription(row?.description || '');
      const { data: notifs } = await supabase
        .from('notifications')
        .select('*')
        .eq('entity', 'organizer_request')
        .eq('target_user_id', uid)
        .order('created_at', { ascending: false })
        .limit(10);
      setNotifications(notifs || []);
    })();
  }, []);

  useEffect(() => {
    if (!uid) return;
    const channel = supabase
      .channel(`organizer-request-notifs-${uid}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: `target_user_id=eq.${uid}` }, (payload) => {
        const n = (payload as any).new;
        if (n && n.entity === 'organizer_request') {
          setNotifications(prev => [n, ...prev].slice(0, 10));
        }
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [uid]);

  const submit = async () => {
    if (!name || !contactEmail) {
      toast.error('Name and contact email are required');
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('request_organizer', { name, image_url: imageUrl, logo_url: logoUrl, contact_email: contactEmail, description });
      if (error) {
        toast.error('Failed to submit request');
        setLoading(false);
        return;
      }
      toast.success('Request submitted');
      const { data: req } = await supabase
        .from('organizer_requests')
        .select('*')
        .eq('id', data as number)
        .maybeSingle();
      setExisting(req);
    } catch {
      toast.error('Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center pt-20">
      <div className="bg-gray-800 rounded-lg shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">Host Events with us</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200">×</button>
        </div>
        <div className="p-6 space-y-6">
          {existing && (
            <div className="bg-gray-700 rounded-lg p-4">
              <p className="text-white font-semibold">Status: {existing.status}</p>
              {existing.admin_message && <p className="text-gray-300 text-sm mt-1">{existing.admin_message}</p>}
              {existing.decided_at && <p className="text-gray-400 text-xs mt-1">Updated: {new Date(existing.decided_at).toLocaleString()}</p>}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Organizer Name</label>
              <input value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Banner Image URL</label>
              <input value={imageUrl} onChange={e => setImageUrl(e.target.value)} className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Contact Email</label>
              <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Logo URL</label>
              <input value={logoUrl} onChange={e => setLogoUrl(e.target.value)} className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Describe your events</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" />
          </div>
          <div className="flex gap-3">
            <button onClick={submit} disabled={loading} className="px-4 py-2 bg-indigo-600 text-white rounded-lg disabled:opacity-50">Submit</button>
            <button onClick={onClose} className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg">Close</button>
          </div>

          {notifications.length > 0 && (
            <div className="mt-8 border-t border-gray-700 pt-6">
              <h3 className="text-lg font-semibold text-white mb-4">Updates</h3>
              <div className="space-y-3">
                {notifications.map(n => (
                  <div key={n.id} className="bg-gray-700 rounded-lg p-4">
                    <p className="text-white font-semibold">{n.action === 'approved' ? 'Request Approved' : n.action === 'rejected' ? 'Request Rejected' : (n.action || 'Update')}</p>
                    <p className="text-gray-300 text-sm">{n.message || 'No note provided'}</p>
                    {n.details?.request_id && (
                      <p className="text-gray-400 text-xs">Request #{n.details.request_id}</p>
                    )}
                    <p className="text-gray-300 text-sm">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}